<template>
    <v-container fluid>
        <v-row class="align-center pl-3">
            <!-- Breadcrumbs -->
            <v-breadcrumbs
                :items="[
                    { text: 'Home', disable: false, href: '/#/' },
                    {
                        text: 'Cost Rates',
                        disable: false,
                        href: '/#/costrates'
                    },
                    { text: 'Business Costs', disable: true }
                ]"
                divider="/"
            ></v-breadcrumbs>
        </v-row>

        <v-row class="ma-0">
            <v-col cols="12" class="py-0">
                <v-progress-linear
                    color="primary"
                    :active="
                        loadingOperatingCostsKey > 0 &&
                            tenantGreenhouses.length == 0
                    "
                    :indeterminate="loadingOperatingCostsKey > 0"
                    height="2"
                ></v-progress-linear>
            </v-col>
        </v-row>

        <v-row
            class="ma-0 mb-5"
            v-for="(greenhouse, index) in tenantGreenhouses"
            :key="index"
        >
            <v-toolbar flat>
                <v-row class="mx-0 px-0 align-center">
                    <h1 class="mr-3">
                        {{ greenhouse.node.name }}
                    </h1>
                    <small v-if="greenhouse.node.totalSpaceM2 > 0"
                        >({{ greenhouse.node.totalSpaceM2 }} m2)</small
                    >
                    <small v-else class="error--text"
                        >({{ greenhouse.node.totalSpaceM2 }} m2)</small
                    >

                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="hasPermission('change_plant')"
                        color="primary"
                        class="mr-3"
                        outlined
                        rounded
                        small
                        :disabled="greenhouse.node.totalSpaceM2 === 0"
                        @click="openNewCostDialog(greenhouse.node.id)"
                        >New Cost</v-btn
                    >
                </v-row>
            </v-toolbar>

            <v-col cols="12">
                <v-data-table
                    dense
                    :headers="headers"
                    :items="greenhouse.node.operatingCostSet.edges"
                    :search="search"
                    :loading="loadingOperatingCostsKey > 0"
                    item-key="node.id"
                    sort-by="node.costName"
                    hide-default-footer
                    disable-pagination
                >
                    <!-- business cost name -->
                    <template v-slot:item.node.costName="{ item }">
                        <v-edit-dialog
                            @open="editingCostName = item.node.costName"
                            @save="
                                updateOperatingCostName(
                                    item.node.id,
                                    editingCostName
                                )
                            "
                        >
                            <span class="primary--text">{{
                                item.node.costName
                            }}</span>
                            <template v-slot:input>
                                <v-text-field
                                    v-model="editingCostName"
                                    :rules="[rules.required]"
                                    label="Edit"
                                    single-line
                                ></v-text-field>
                            </template>
                        </v-edit-dialog>
                    </template>

                    <!-- Month cost values -->
                    <template
                        v-for="mon in months"
                        v-slot:[mon.m_slot]="{ item }"
                    >
                        <v-edit-dialog
                            :key="mon.m_text"
                            @open="
                                editingCostValue = getMonthCostValueFromOperatingCostValueByMonthSet(
                                    item.node.operatingCostValueByMonthSet
                                        .edges,
                                    mon.m_value
                                )
                            "
                            @save="
                                saveOperatingCostMonthValue(
                                    item.node.id,
                                    mon.m_value,
                                    editingCostValue
                                )
                            "
                        >
                            <span class="primary--text"
                                >€{{
                                    getMonthCostValueFromOperatingCostValueByMonthSet(
                                        item.node.operatingCostValueByMonthSet
                                            .edges,
                                        mon.m_value
                                    ).toLocaleString()
                                }}</span
                            >
                            <template v-slot:input>
                                <v-text-field
                                    v-model="editingCostValue"
                                    :rules="[rules.required]"
                                    label="Edit"
                                    type="number"
                                    single-line
                                ></v-text-field>
                            </template>
                        </v-edit-dialog>
                    </template>

                    <template v-slot:item.action="{ item }">
                        <v-btn
                            icon
                            small
                            color="grey"
                            @click="openDeleteOperatingCostDialog(item.node.id)"
                        >
                            <v-icon>delete</v-icon>
                        </v-btn>
                    </template>

                    <!-- Total cost -->
                    <template slot="body.append">
                        <tr>
                            <th class="subtitle-2">Total</th>
                            <td
                                v-for="mon in months"
                                :key="mon.m_text"
                                class="text-right"
                            >
                                <!-- €{{ getMonthTotalOperatingCost(mon.m_value) }} -->
                                €{{
                                    getMonthTotalCostFromOperatingCostRateSet(
                                        greenhouse.node.operatingCostRateSet
                                            .edges,
                                        mon.m_value
                                    ).toLocaleString()
                                }}
                            </td>
                            <th></th>
                        </tr>
                    </template>

                    <!-- Cost rate -->
                    <template slot="body.append">
                        <tr>
                            <th class="subtitle-2">Cost / m2.day</th>
                            <th
                                v-for="mon in months"
                                :key="mon.m_text"
                                class="subtitle-2 text-right"
                            >
                                <!-- €{{ getMonthOperatingCostRate(mon.m_value) }} -->
                                €{{
                                    getMonthCostRateFromOperatingCostRateSet(
                                        greenhouse.node.operatingCostRateSet
                                            .edges,
                                        mon.m_value
                                    ).toLocaleString()
                                }}
                            </th>
                            <th></th>
                        </tr>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <!-- New Cost Dialog -->
        <v-dialog v-model="newCostDialog" persistent max-width="500px">
            <v-card>
                <v-card-title class="primary white--text">
                    <span class="headline">New Business Cost</span>
                </v-card-title>

                <v-container>
                    <v-form ref="newCostForm" v-model="newCostFormValid">
                        <v-row class="mx-0">
                            <v-col cols="12">
                                <v-text-field
                                    v-model="newCost.costName"
                                    :rules="[rules.required]"
                                    label="Cost name*"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-container>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="closeNewCostDialog"
                        >Cancel</v-btn
                    >
                    <v-btn
                        color="primary"
                        :loading="isSavingNewOperatingCost"
                        :disabled="!newCostFormValid"
                        @click="saveNewCost()"
                        >Save</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Cost Dialog -->
        <DeleteDialog
            :dialog.sync="deleteOperatingCostsDialog"
            v-on:confirmed="deleteOperatingCost"
        >
            <template v-slot:title>Delete Selected Row</template>
            <template v-slot:content
                >Are you sure you want to delete selected cost?
                <br />
            </template>
        </DeleteDialog>
    </v-container>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import DeleteDialog from "@/components/base/DeleteDialog.vue";

export default {
    name: "operating-costs-page",
    components: { DeleteDialog },

    apollo: {
        tenantGreenhouses: {
            query: gql`
                query tenantGreenhouses {
                    tenantGreenhouses(orderBy: "name") {
                        edges {
                            node {
                                id
                                name
                                totalSpaceM2
                                operatingCostSet {
                                    edges {
                                        node {
                                            id
                                            costName
                                            operatingCostValueByMonthSet {
                                                edges {
                                                    node {
                                                        id
                                                        month
                                                        costValue
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                operatingCostRateSet {
                                    edges {
                                        node {
                                            id
                                            month
                                            costPerM2Day
                                            totalCost
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {};
            },
            // result({ data, loading }) {
            //     if (!loading) {
            //         this.calculateOperatingCosts(
            //             data.tenantOperatingCosts.edges
            //         );
            //     }
            // },
            loadingKey: "loadingOperatingCostsKey",
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.tenantGreenhouses.edges,
            skip: false
            // pollInterval: 60000 // ms
        }
        // tenantOperatingCostRates: {
        //     query: gql`
        //         query tenantOperatingCostRates {
        //             tenantOperatingCostRates {
        //                 id
        //                 month
        //                 totalCost
        //                 costPerM2Day
        //             }
        //         }
        //     `,
        //     variables() {
        //         return {};
        //     },
        //     // Loading state
        //     // loadingKey is the name of the data property
        //     // that will be incremented when the query is loading
        //     // and decremented when it no longer is.
        //     loadingKey: "loadingOperatingCostsKey",
        //     // Additional options here
        //     fetchPolicy: "cache-and-network",
        //     update: data => data.tenantOperatingCostRates,
        //     skip: false
        //     // pollInterval: 60000 // ms
        // }
    },

    data() {
        return {
            loadingOperatingCostsKey: 0,
            isSavingNewOperatingCost: false,
            isDeletingOperatingCosts: false,
            deleteOperatingCostsDialog: false,
            // editingTenantAvailableSpace: null,
            newCostDialog: false,
            newCost: {},
            newCostFormValid: true,
            editingCostName: null,
            editingCostValue: null,
            selectedGreenhouseNodeId: null,
            selectedOperatingCostNodeId: null,
            selectedOperatingCosts: [],
            search: null,
            months: [
                { m_text: "jan", m_slot: "item.node.jan", m_value: 1 },
                { m_text: "feb", m_slot: "item.node.feb", m_value: 2 },
                { m_text: "mar", m_slot: "item.node.mar", m_value: 3 },
                { m_text: "apr", m_slot: "item.node.apr", m_value: 4 },
                { m_text: "may", m_slot: "item.node.may", m_value: 5 },
                { m_text: "jun", m_slot: "item.node.jun", m_value: 6 },
                { m_text: "jul", m_slot: "item.node.jul", m_value: 7 },
                { m_text: "aug", m_slot: "item.node.aug", m_value: 8 },
                { m_text: "sep", m_slot: "item.node.sep", m_value: 9 },
                { m_text: "oct", m_slot: "item.node.oct", m_value: 10 },
                { m_text: "nov", m_slot: "item.node.nov", m_value: 11 },
                { m_text: "dec", m_slot: "item.node.dec", m_value: 12 }
            ],
            headers: [
                {
                    text: "Cost",
                    align: "start",
                    sortable: true,
                    value: "node.costName"
                },
                { text: "Jan", value: "node.jan", align: "end" },
                { text: "Feb", value: "node.feb", align: "end" },
                { text: "Mar", value: "node.mar", align: "end" },
                { text: "Apr", value: "node.apr", align: "end" },
                { text: "May", value: "node.may", align: "end" },
                { text: "Jun", value: "node.jun", align: "end" },
                { text: "Jul", value: "node.jul", align: "end" },
                { text: "Aug", value: "node.aug", align: "end" },
                { text: "Sep", value: "node.sep", align: "end" },
                { text: "Oct", value: "node.oct", align: "end" },
                { text: "Nov", value: "node.nov", align: "end" },
                { text: "Dec", value: "node.dec", align: "end" },
                {
                    text: "Actions",
                    value: "action",
                    width: "120px",
                    align: "center",
                    sortable: false
                }
            ],
            tenantGreenhouses: [],
            operatingCosts: [],
            // operatingCostRates: [],
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
                // nameRules: [
                //     v => !!v || "Name is required",
                //     v =>
                //         v.length <= 50 || "Name must be less than 50 characters"
                // ]
            }
        };
    },
    computed: {
        me() {
            return this.$store.state.user.me;
        },
        tenant() {
            if (!this.me || !this.me.tenantUser) {
                return null;
            }
            return this.me.tenantUser.tenant;
        },
        // totalSpaceM2() {
        //     if (!this.me || !this.me.tenantUser) {
        //         return null;
        //     }
        //     return this.me.tenantUser.tenant.totalAvailableSpaceM2;
        // },
        totalCostByMonth() {
            var _totalCostByMonth = {};

            this.months.forEach(mon => {
                const _monCostList = this.operatingCosts.map(
                    item => item.node[mon.m_text]
                );
                const _monTotalCost = _monCostList.reduce(function(acc, cost) {
                    return acc + cost;
                }, 0);

                _totalCostByMonth[mon.m_text] = _monTotalCost;
            });

            return _totalCostByMonth;
        },
        costRateByMonth() {
            var _costRateByMonth = {};

            this.months.forEach(mon => {
                const _daysInMonth = this.getDaysOfMonth(mon.m_value);

                _costRateByMonth[mon.m_text] =
                    this.totalCostByMonth[mon.m_text] /
                        _daysInMonth /
                        this.totalSpaceM2 || 0;
            });

            return _costRateByMonth;
        }
    },
    watch: {},
    created() {
        this.hasPermission = helper.hasPermission;
        // this.fetchMe();
    },
    methods: {
        // fetchMe() {
        //     this.$store
        //         .dispatch("user/fetchMe")
        //         .then(() => {})
        //         .catch(error => {
        //             console.log(error);
        //         });
        // },

        refreshOperatingCostData() {
            this.$apollo.queries.tenantGreenhouses.refresh();
            // this.$apollo.queries.tenantOperatingCostRates.refresh();
        },

        getCostMonthValue(costValues, month) {
            if (!costValues || !costValues.length) {
                return 0;
            }

            const obj = costValues.find(
                element => element.node.month === month
            );

            if (!obj) {
                return 0;
            }

            return obj.node.costValue;
        },

        calculateOperatingCosts(tenantOperatingCosts) {
            this.operatingCosts = tenantOperatingCosts.map(item => {
                this.months.forEach(mon => {
                    item.node[mon.m_text] = this.getCostMonthValue(
                        item.node.operatingCostValueByMonthSet.edges,
                        mon.m_value
                    );
                });

                return item;
            });
        },

        getMonthCostValueFromOperatingCostValueByMonthSet(
            operatingCostValueByMonthSet,
            month
        ) {
            // console.log(
            //     "operatingCostValueByMonthSet: ",
            //     operatingCostValueByMonthSet,
            //     " month: ",
            //     month
            // );

            let _costValue = 0;

            let _operatingCostValueByMonth = operatingCostValueByMonthSet.find(
                item => {
                    return item.node.month == month;
                }
            );

            if (_operatingCostValueByMonth) {
                _costValue = _operatingCostValueByMonth.node.costValue;
            }

            return _costValue;
        },

        getMonthCostRateFromOperatingCostRateSet(operatingCostRateSet, month) {
            let _costValueRate = 0;

            let _operatingCostRate = operatingCostRateSet.find(item => {
                return item.node.month == month;
            });

            if (_operatingCostRate) {
                _costValueRate = _operatingCostRate.node.costPerM2Day;
            }

            return _costValueRate.toFixed(3);
        },

        getMonthTotalCostFromOperatingCostRateSet(operatingCostRateSet, month) {
            let _totalCost = 0;

            let _operatingCostRate = operatingCostRateSet.find(item => {
                return item.node.month == month;
            });

            if (_operatingCostRate) {
                _totalCost = _operatingCostRate.node.totalCost;
            }

            return _totalCost;
        },

        // getMonthTotalOperatingCost(monthNumber) {
        //     if (
        //         !this.tenantOperatingCostRates ||
        //         this.tenantOperatingCostRates.length === 0
        //     ) {
        //         return 0;
        //     }

        //     const _costRate = this.tenantOperatingCostRates.find(
        //         element => element.month === monthNumber
        //     );

        //     if (_costRate == null) {
        //         return 0;
        //     }

        //     return _costRate.totalCost.toLocaleString();
        // },

        // getMonthOperatingCostRate(monthNumber) {
        //     if (
        //         !this.tenantOperatingCostRates ||
        //         this.tenantOperatingCostRates.length === 0
        //     ) {
        //         return 0;
        //     }

        //     const _costRate = this.tenantOperatingCostRates.find(
        //         element => element.month === monthNumber
        //     );

        //     if (_costRate == null) {
        //         return 0;
        //     }

        //     return _costRate.costPerM2Day.toFixed(2).toLocaleString();
        // },

        getDaysOfMonth(monthNumber) {
            const _daysInMonth = new Date(2021, monthNumber, 0).getDate();
            return _daysInMonth;
        },

        updateOperatingCostName(operatingCostNodeId, newCostName) {
            // check cost name is empty
            if (newCostName == "") {
                // show snackbar
                const payload = {
                    color: "error",
                    message: `Invalid value!`
                };
                this.$store.dispatch("snackbar/showMessage", payload);

                return;
            }

            // check if cost name already exists
            if (
                this.operatingCosts.some(item => {
                    return (
                        item.node.id != operatingCostNodeId &&
                        item.node.costName.toLowerCase() ==
                            newCostName.toLowerCase()
                    );
                })
            ) {
                // show snackbar
                const payload = {
                    color: "error",
                    message: `Duplicate cost name!`
                };
                this.$store.dispatch("snackbar/showMessage", payload);

                return;
            }

            // // console.log(operatingCostNodeId, month, costValue);
            // this.loadingOperatingCostsKey = true;
            this.loadingOperatingCostsKey++;

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation updateOperatingCostName(
                            $input: UpdateOperatingCostNameInput!
                        ) {
                            updateOperatingCostName(input: $input) {
                                operatingCost {
                                    id
                                    costName
                                    operatingCostValueByMonthSet {
                                        edges {
                                            node {
                                                id
                                                month
                                                costValue
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            id: operatingCostNodeId,
                            costName: newCostName
                        }
                    }
                })
                .then(() => {
                    // this.isLoadingPlannedTaskPhases = false;
                    // console.log(response);

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Saved!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // this.isLoadingPlannedTaskPhases = false;

                    // Error
                    console.error(error);

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: `Failed!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    // this.loadingOperatingCostsKey = false;
                    this.loadingOperatingCostsKey--;
                });
        },

        // updateTenantAvailableSpace() {
        //     // check cost name is empty
        //     if (this.editingTenantAvailableSpace <= 0) {
        //         // show snackbar
        //         const payload = {
        //             color: "error",
        //             message: `Invalid value!`
        //         };
        //         this.$store.dispatch("snackbar/showMessage", payload);

        //         return;
        //     }

        //     // console.log(operatingCostNodeId, month, costValue);
        //     this.loadingOperatingCostsKey++;

        //     this.$apollo
        //         .mutate({
        //             // Query
        //             mutation: gql`
        //                 mutation updateTenantAvailableSpace(
        //                     $input: UpdateTenantAvailableSpaceInput!
        //                 ) {
        //                     updateTenantAvailableSpace(input: $input) {
        //                         tenant {
        //                             id
        //                             name
        //                             totalAvailableSpaceM2
        //                         }
        //                     }
        //                 }
        //             `,
        //             // Parameters
        //             variables: {
        //                 input: {
        //                     id: this.tenant.id,
        //                     totalAvailableSpaceM2: this
        //                         .editingTenantAvailableSpace
        //                 }
        //             }
        //         })
        //         .then(() => {
        //             this.refreshOperatingCostData();

        //             this.fetchMe();

        //             // show snackbar
        //             const payload = {
        //                 color: "success",
        //                 message: `Saved!`
        //             };
        //             this.$store.dispatch("snackbar/showMessage", payload);
        //         })
        //         .catch(error => {
        //             // this.isLoadingPlannedTaskPhases = false;

        //             // Error
        //             console.error(error);

        //             // show snackbar
        //             const payload = {
        //                 color: "error",
        //                 message: `Failed!`
        //             };
        //             this.$store.dispatch("snackbar/showMessage", payload);
        //         })
        //         .finally(() => {
        //             this.loadingOperatingCostsKey--;
        //         });
        // },

        saveOperatingCostMonthValue(operatingCostNodeId, month, costValue) {
            // check input value
            if (costValue === "") {
                // show snackbar
                const payload = {
                    color: "error",
                    message: `Invalid value!`
                };
                this.$store.dispatch("snackbar/showMessage", payload);

                return;
            }

            // console.log(operatingCostNodeId, month, costValue);
            // this.loadingOperatingCostsKey = true;
            this.loadingOperatingCostsKey++;

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation updateOperatingCostMonthValue(
                            $input: UpdateOperatingCostMonthValueInput!
                        ) {
                            updateOperatingCostMonthValue(input: $input) {
                                operatingCost {
                                    id
                                    costName
                                    operatingCostValueByMonthSet {
                                        edges {
                                            node {
                                                id
                                                month
                                                costValue
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            operatingCostNodeId: operatingCostNodeId,
                            monthCostValue: {
                                month: month,
                                costValue: costValue
                            }
                        }
                    }
                })
                .then(() => {
                    // this.isLoadingPlannedTaskPhases = false;
                    // console.log(response);
                    this.refreshOperatingCostData();

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Saved!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // this.isLoadingPlannedTaskPhases = false;

                    // Error
                    console.error(error);

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: `Failed!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    // this.loadingOperatingCostsKey = false;
                    this.loadingOperatingCostsKey--;
                });
        },

        openNewCostDialog(greenhouseNodeId) {
            this.selectedGreenhouseNodeId = greenhouseNodeId;
            this.newCostDialog = true;
        },

        closeNewCostDialog() {
            this.newCostDialog = false;
            this.newCost = {};

            if (this.$refs.newCostForm) {
                this.$refs.newCostForm.reset();
            }
        },

        saveNewCost() {
            const costName = this.newCost.costName;

            // check cost name is empty
            if (costName == "") {
                // show snackbar
                const payload = {
                    color: "error",
                    message: `Invalid value!`
                };
                this.$store.dispatch("snackbar/showMessage", payload);

                return;
            }

            // check if cost name already exists
            if (
                this.operatingCosts.some(item => {
                    return (
                        item.node.costName.toLowerCase() ==
                        costName.toLowerCase()
                    );
                })
            ) {
                // show snackbar
                const payload = {
                    color: "error",
                    message: `Duplicate cost name!`
                };
                this.$store.dispatch("snackbar/showMessage", payload);

                return;
            }

            // console.log(operatingCostNodeId, month, costValue);
            this.isSavingNewOperatingCost = true;

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation createOperatingCost(
                            $input: CreateOperatingCostInput!
                        ) {
                            createOperatingCost(input: $input) {
                                operatingCost {
                                    id
                                    costName
                                    operatingCostValueByMonthSet {
                                        edges {
                                            node {
                                                id
                                                month
                                                costValue
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            greenhouseNodeId: this.selectedGreenhouseNodeId,
                            costName: costName
                        }
                    }
                })
                .then(() => {
                    this.closeNewCostDialog();
                    this.refreshOperatingCostData();

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Saved!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // this.isLoadingPlannedTaskPhases = false;

                    // Error
                    console.error(error);

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: `Failed!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    this.isSavingNewOperatingCost = false;
                });
        },

        openDeleteOperatingCostDialog(operatingCostNodeId) {
            this.selectedOperatingCostNodeId = operatingCostNodeId;

            this.deleteOperatingCostsDialog = true;
        },

        deleteOperatingCost() {
            this.isDeletingOperatingCosts = true;

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation deleteOperatingCost(
                            $input: DeleteOperatingCostInput!
                        ) {
                            deleteOperatingCost(input: $input) {
                                operatingCost {
                                    id
                                    costName
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            id: this.selectedOperatingCostNodeId
                        }
                    }
                })
                .then(() => {
                    this.selectedOperatingCosts = [];
                    this.refreshOperatingCostData();

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Deleted!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // this.isLoadingPlannedTaskPhases = false;

                    // Error
                    console.error(error);

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: `Failed!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    this.isDeletingOperatingCosts = false;
                    this.deleteOperatingCostsDialog = false;
                });
        },

        batchDeleteOperatingCosts() {
            this.isDeletingOperatingCosts = true;

            if (this.selectedOperatingCosts.length == 0) {
                // show snackbar
                const payload = {
                    color: "error",
                    message: `No item selected!`
                };
                this.$store.dispatch("snackbar/showMessage", payload);

                return;
            }

            var _operatingCostList = this.selectedOperatingCosts.map(item => {
                return item.node.id;
            });

            // console.log(_operatingCostList);

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation batchDeleteOperatingCosts(
                            $input: BatchDeleteOperatingCostsInput!
                        ) {
                            batchDeleteOperatingCosts(input: $input) {
                                operatingCosts {
                                    id
                                    costName
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            ids: _operatingCostList
                        }
                    }
                })
                .then(() => {
                    this.selectedOperatingCosts = [];
                    this.refreshOperatingCostData();

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Deleted!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // this.isLoadingPlannedTaskPhases = false;

                    // Error
                    console.error(error);

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: `Failed!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    this.isDeletingOperatingCosts = false;
                    this.deleteOperatingCostsDialog = false;
                });
        }
    }
};
</script>